import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Users, GitBranch, GraduationCap, KeyRound, FolderLock, Download, Cake, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { canManageUsers, isHrHead } from '@/lib/permissions';
import PeopleTab from '@/components/PeopleTab';
import ReportingTree from '@/components/ReportingTree';
import DivisionTrainingCenter from '@/components/DivisionTrainingCenter';
import UserAdminTable from '@/components/UserAdminTable';

const DIVISION_NAMES = { me: 'MindEscapes', tdb: 'The Doggy Bakery', lc: 'Les Concierges', 'cc-clubs': 'Club Concierge', 'cc-ops': 'CC Residential', cs: 'Club Social' };

function HRStat({ label, value, tone, testId, onClick, hint }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} title={hint} className={`rounded-lg border bg-white px-4 py-3 min-w-[130px] text-left ${onClick ? 'hover:shadow-md hover:-translate-y-0.5 transition-transform cursor-pointer' : ''}`} style={{ borderColor: onClick ? '#2A7B88' : '#E5E1DC' }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ color: '#6B7280' }}>{label}{onClick && <Download size={10} style={{ color: '#2A7B88' }} />}</div>
      <div className="text-xl font-bold mt-0.5" style={{ color: tone || '#1B2A4A' }}>{value ?? '—'}</div>
    </Tag>
  );
}

function TodayNudge({ divisionId }) {
  const [n, setN] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState('');
  useEffect(() => { api.get(`/admin/hr-nudge/preview?division_id=${divisionId}`).then(r => setN(r.data)).catch(() => setN(null)); }, [divisionId]);
  const sendNow = async () => {
    setSending(true); setSentMsg('');
    try {
      const r = await api.post(`/admin/hr-nudge/run-now?division_id=${divisionId}`);
      const rec = r.data?.recipients || [];
      setSentMsg(`Sent to ${rec.length} HR head${rec.length === 1 ? '' : 's'} · WA: ${rec.map(x => x.wa).join('/') || '—'} · Email: ${rec.map(x => x.email_status).join('/') || '—'}`);
    } catch (e) { setSentMsg(e?.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };
  if (!n) return null;
  const items = [
    ...n.birthdays.map(b => `🎂 ${b.name}`),
    ...n.wedding_anniversaries.map(w => `💍 ${w.name}`),
    ...n.work_anniversaries.map(w => `🏅 ${w.name} · ${w.years} yr`),
  ];
  return (
    <div className="rounded-lg border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" style={{ borderColor: '#F5C86B', backgroundColor: '#FFFBEB' }} data-testid="today-nudge-panel">
      <Cake size={16} style={{ color: '#BA7517' }} className="shrink-0" />
      <div className="flex-1 text-xs" style={{ color: '#92400E' }}>
        <b>Today's nudge</b> ({n.date}) — {items.length ? items.join(' · ') : 'no birthdays or anniversaries today'}. Auto-sent to HR heads on WhatsApp + email at 08:00 IST.
        {sentMsg && <div className="mt-1" data-testid="today-nudge-result">{sentMsg}</div>}
      </div>
      <button onClick={sendNow} disabled={sending} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#BA7517' }} data-testid="send-nudge-now-btn"><Send size={12} /> {sending ? 'Sending…' : 'Send now'}</button>
    </div>
  );
}

function HRUsersTab({ divisionId }) {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => { api.get('/admin/users').then(r => setUsers(r.data || [])).catch(() => setUsers([])); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="hr-users-tab">
      <div>
        <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>ScaleBoard logins — {DIVISION_NAMES[divisionId] || divisionId}</h3>
        <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Create logins for new joiners, reset passwords (resets to Welcome@2026), suspend or deactivate leavers. CEO accounts and other divisions are outside HR scope.</p>
      </div>
      <UserAdminTable users={users} divisions={[{ id: divisionId, name: DIVISION_NAMES[divisionId] || divisionId }]} onRefresh={load} hrMode />
    </div>
  );
}

export default function HRHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const divisionId = user?.division_id || 'me';
  const divisionName = DIVISION_NAMES[divisionId] || divisionId;
  const showUsers = canManageUsers(user);
  const tab = params.get('tab') || 'people';
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get(`/people?division_id=${divisionId}&include_inactive=true`).catch(() => ({ data: [] })),
      api.get(`/people/upcoming-milestones?division_id=${divisionId}&days=30`).catch(() => ({ data: { count: 0 } })),
      api.get(`/people/warnings?division_id=${divisionId}`).catch(() => ({ data: { total: 0 } })),
    ]).then(([pRes, mRes, wRes]) => {
      const people = pRes.data || [];
      const active = people.filter(p => p.status !== 'inactive');
      setStats({
        active: active.length,
        inactive: people.length - active.length,
        noContact: active.filter(p => !(p.mobile || p.phone_personal)).length,
        milestones: mRes.data?.count || 0,
        warnings: wRes.data?.total || 0,
      });
    });
  }, [user, divisionId]);

  const downloadGapSheet = async () => {
    const r = await api.get(`/people/hr-master/missing-contacts-csv?division_id=${divisionId}`, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
    a.download = `missing_contacts_${divisionId}.csv`; a.click();
  };

  const allowed = user && (user.role === 'ceo' || (user.scopes || []).includes('people') || ['leader', 'sub_leader', 'submitter'].includes(user.role));
  if (user && !allowed) return <Navigate to={`/division/${divisionId}`} replace />;

  const tabs = [
    { id: 'people', label: 'People', icon: Users },
    { id: 'org-tree', label: 'Org Tree', icon: GitBranch },
    { id: 'training', label: 'Training', icon: GraduationCap },
    ...(showUsers ? [{ id: 'users', label: 'Logins', icon: KeyRound }] : []),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="hr-hub-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: '#BA7517' }}>{divisionName} · {isHrHead(user) ? 'HR Head' : 'People'}</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: 'Georgia, serif', color: '#1B2A4A' }} data-testid="hr-hub-title">HR Hub</h1>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Roster · KYC documents vault · employee kit & expenses · training · reporting tree{showUsers ? ' · logins' : ''}. Click any name to open the full profile.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <HRStat label="Active staff" value={stats?.active} testId="hr-stat-active" />
          <HRStat label="Exited / inactive" value={stats?.inactive} testId="hr-stat-inactive" />
          <HRStat label="Missing mobile" value={stats?.noContact} tone={stats?.noContact ? '#D4726A' : undefined} testId="hr-stat-missing-contact" onClick={downloadGapSheet} hint="Download the gap sheet (name + blank mobile/email) → fill in Excel → Import HR Master" />
          <HRStat label="Milestones · 30d" value={stats?.milestones} tone="#BA7517" testId="hr-stat-milestones" />
          <HRStat label="Attention flags" value={stats?.warnings} tone={stats?.warnings ? '#D4726A' : undefined} testId="hr-stat-warnings" />
        </div>
      </div>

      {(isHrHead(user) || user?.role === 'ceo') && <TodayNudge divisionId={divisionId} />}

      <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: '#E5E1DC' }} data-testid="hr-hub-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setParams({ tab: t.id })} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? '' : 'border-transparent hover:text-[#1B2A4A]'}`} style={tab === t.id ? { color: '#1B2A4A', borderColor: '#1B2A4A' } : { color: '#6B7280' }} data-testid={`hr-tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'people' && <PeopleTab divisionId={divisionId} divisionName={divisionName} />}

      {tab === 'org-tree' && (
        <div className="space-y-3" data-testid="hr-org-tree">
          <div>
            <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>Reporting Tree — {divisionName}</h3>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Drag a person onto a new manager to reassign. Click any name to open their profile.</p>
          </div>
          <ReportingTree divisionId={divisionId} canEdit={user?.role === 'ceo' || user?.role === 'leader' || isHrHead(user)} onPersonClick={(n) => navigate(`/people/${n.id}`)} />
        </div>
      )}

      {tab === 'training' && <DivisionTrainingCenter divisionId={divisionId} isCEO={user?.role === 'ceo'} />}

      {tab === 'users' && showUsers && <HRUsersTab divisionId={divisionId} />}

      <div className="rounded-lg border px-4 py-3 text-xs flex items-start gap-2" style={{ borderColor: '#E5E1DC', backgroundColor: '#FFFFFF', color: '#6B7280' }} data-testid="hr-hub-footer-note">
        <FolderLock size={14} className="mt-0.5 shrink-0" style={{ color: '#2A7B88' }} />
        <span>KYC documents (Aadhaar, PAN, offer letter, bank proof, photo) live inside each person's profile → <b>Documents</b> tab. Uniform, accommodation, assets issued, petty cash and expense claims are under <b>Kit &amp; Expenses</b>. Expiring documents trigger the 09:00 IST reminder email/WhatsApp to the HR heads.</span>
      </div>
    </div>
  );
}
